import React, { useEffect, useRef, useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'

import { FlexPortionEditor } from './FlexPortionEditor'
import { FlexPortionSummary } from './FlexPortionSummary'

const EDITOR_PANEL_ANIMATION_MS = 260
const EDITOR_PANEL_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
const EDITOR_PANEL_MAX_HEIGHT = 420
const COLLAPSED_FOOTER_HEIGHT = 153

const EditorPanel = styled.div<{ isOpen: boolean }>(({ isOpen }) => ({
  maxHeight: `${isOpen ? EDITOR_PANEL_MAX_HEIGHT : COLLAPSED_FOOTER_HEIGHT}px`,
  opacity: isOpen ? 1 : 0,
  overflow: 'hidden',
  transform: isOpen ? 'translateY(0)' : 'translateY(4px)',
  transition: [
    `max-height ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
    `opacity ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
    `transform ${EDITOR_PANEL_ANIMATION_MS}ms ${EDITOR_PANEL_EASING}`,
  ].join(', '),

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
}))

const EditorPanelContent = styled.div({
  overflow: 'hidden',
})

type FlexPortionFooterProps = {
  recipeCount: number
  numPortions: number
  onUpdatePortions: (numPortions: number) => void
  onRemoveRecipe: () => void
}

export const FlexPortionFooter = ({
  recipeCount,
  numPortions,
  onUpdatePortions,
  onRemoveRecipe,
}: FlexPortionFooterProps) => {
  const [isEditorMounted, setIsEditorMounted] = useState(false)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const animationFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (isEditorOpen || !isEditorMounted) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setIsEditorMounted(false)
    }, EDITOR_PANEL_ANIMATION_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isEditorOpen, isEditorMounted])

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [])

  const openEditor = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
    }

    setIsEditorOpen(false)
    setIsEditorMounted(true)

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsEditorOpen(true)
      animationFrameRef.current = null
    })
  }

  const closeEditor = () => {
    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    setIsEditorOpen(false)
  }

  const handleUpdatePortions = (updatedNumPortions: number) => {
    onUpdatePortions(updatedNumPortions)
    closeEditor()
  }

  const handleRemoveRecipe = () => {
    onRemoveRecipe()
    closeEditor()
  }

  if (isEditorMounted) {
    return (
      <EditorPanel data-testid="flex-portion-footer-editor" isOpen={isEditorOpen}>
        <EditorPanelContent>
          <FlexPortionEditor
            recipeCount={recipeCount}
            numPortions={numPortions}
            onClose={closeEditor}
            onUpdatePortions={handleUpdatePortions}
            onRemoveRecipe={handleRemoveRecipe}
          />
        </EditorPanelContent>
      </EditorPanel>
    )
  }

  return (
    <Box data-testid="flex-portion-footer" width="100%">
      <Box
        alignItems={AlignItems.Center}
        borderBottomWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        display={Display.Flex}
        justifyContent={JustifyContent.FlexEnd}
        paddingH={3}
        paddingBottom={1}
      >
        <FlexPortionSummary
          recipeCount={recipeCount}
          portionCount={numPortions}
          onEdit={openEditor}
        />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} paddingH={3} paddingV={2}>
        <Button disabled width="100%">
          Recipe Added
        </Button>
        <Space direction="vertical" size={2} />
        <Button
          colorVariant={ButtonColorVariant.Tertiary}
          onClick={handleRemoveRecipe}
          width="100%"
        >
          <Icon name="delete" variant={IconVariant.Actionable} />
          <Space direction="horizontal" size={1} />
          Remove recipe
        </Button>
      </Box>
    </Box>
  )
}
